/**
 * Словарь продуктовых событий (FN-1.2) — зеркало серверного
 * `backend/src/main/java/com/vrmessenger/analytics/AnalyticsEventName.java`.
 *
 * Сервер принимает только имена из своего перечисления и молча отбрасывает остальные, поэтому
 * расхождение между этими двумя списками не падает, а тихо обнуляет метрику. Чтобы этого не
 * случалось, соответствие проверяется тестом `__tests__/analyticsEvents.test.ts`: он читает
 * java-файл и сверяет списки.
 *
 * Свойства тоже не свободные: всё, чего нет в `allowedProps` соответствующего события на сервере,
 * выбрасывается при приёме. Так текст сообщения или название чата не может попасть в аналитику по
 * недосмотру — для него просто нет разрешённого ключа.
 */
export type AnalyticsEventName =
  | 'session_start'
  | 'session_end'
  | 'login_success'
  | 'login_failed'
  | 'logout'
  | 'signup_completed'
  | 'chat_opened'
  | 'message_sent'
  | 'call_started'
  | 'call_ended'
  | 'channel_post_published'
  | 'channel_post_viewed'
  | 'channel_subscribed'
  | 'channel_unsubscribed'
  | 'channel_shared'
  | 'channel_comment_sent'
  | 'paywall_viewed'
  | 'trial_started'
  | 'purchase_completed'
  | 'subscription_renewed'
  | 'subscription_cancelled';

/** Только скаляры: вложенный объект сервер выбросит, а мы бы зря его тащили по сети. */
export type AnalyticsProps = Record<string, string | number | boolean>;

/** Что именно отправили — событие `message_sent`. */
type AnalyticsMessageKind =
  | 'text'
  | 'photo'
  | 'video'
  | 'voice'
  | 'file'
  | 'contact'
  | 'checklist'
  | 'other';

/**
 * Тип payload-а → метка для аналитики.
 *
 * Отдельная функция, а не поле в самом payload-е: словарь аналитики должен уметь меняться, не
 * трогая формат сообщений, а формат сообщений — не тянуть за собой правку метрик.
 */
export function messageKindFromPayloadType(type: string): AnalyticsMessageKind {
  switch (type) {
    case 'text':
      return 'text';
    case 'image':
      return 'photo';
    case 'video':
      return 'video';
    case 'audio':
      return 'voice';
    case 'contact_card':
      return 'contact';
    case 'checklist':
      return 'checklist';
    default:
      return 'other';
  }
}

/**
 * Длительность в секундах → корзина.
 *
 * В аналитику уезжает корзина, а не само число: точная длительность звонка или сессии — это уже
 * след поведения конкретного человека, а для продуктовых вопросов («короткие звонки или длинные»)
 * достаточно порядка величины.
 */
export function durationBucketSeconds(seconds: number): number {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return 0;
  }
  const buckets = [5, 15, 30, 60, 180, 600, 1800, 3600];
  for (const bucket of buckets) {
    if (seconds <= bucket) {
      return bucket;
    }
  }
  return 7200;
}
